import React, { useState } from 'react';

import { LENS_MODAL_HEADER, lensTokens } from '~/configs/unifiedAnalyticsTheme';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';

import { formatRelativeTime } from '~/pages/AskRichardUnified/chat/SessionHistoryPanel';

import {
  type LensViewersDays,
  useGetReportingLeLensViewers,
} from '~/hooks/apis/reporting/useGetReportingLeLensViewers';

import type { Lens } from '~/types/leLens';

import { ownerDisplayName } from '../lensLibrary';

type Props = {
  lens: Pick<Lens, 'id' | 'title' | 'ownerEmail'>;
  open: boolean;
  onClose: () => void;
};

const DAY_OPTIONS: Array<LensViewersDays> = [7, 30, 90];

export default function LensViewersDialog({ lens, open, onClose }: Props) {
  const tokens = lensTokens(useTheme());
  const [days, setDays] = useState<LensViewersDays>(30);
  const { data, isLoading, error } = useGetReportingLeLensViewers(lens.id, days, open);
  const now = Date.now();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="lens-viewers-title"
      PaperProps={{ sx: { bgcolor: tokens.canvas, borderRadius: '8px' } }}
    >
      <DialogTitle id="lens-viewers-title" sx={LENS_MODAL_HEADER}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Typography component="span" sx={{ fontSize: 18, fontWeight: 600 }}>
            Who viewed {lens.title}
          </Typography>
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <ToggleButtonGroup
          exclusive
          value={days}
          onChange={(_event, value: LensViewersDays | null) => {
            if (value !== null) {
              setDays(value);
            }
          }}
          size="small"
          aria-label="Views period"
          sx={{ my: 2 }}
        >
          {DAY_OPTIONS.map((option) => (
            <ToggleButton key={option} value={option}>
              {option} days
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {isLoading && (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={24} aria-label="Loading viewers" />
          </Stack>
        )}
        {error && <Alert severity="error">{error.message}</Alert>}
        {!isLoading && !error && data && (
          <Table size="small" aria-label="Lens viewers">
            <TableHead>
              <TableRow>
                <TableCell>Person</TableCell>
                <TableCell align="right">Views</TableCell>
                <TableCell>Last viewed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.viewers.map((viewer) => (
                <TableRow key={viewer.email}>
                  <TableCell>
                    {ownerDisplayName(viewer.email)}
                    {viewer.email.toLowerCase() === lens.ownerEmail.toLowerCase() && (
                      <Typography component="span" color="text.secondary" sx={{ fontSize: 11, ml: 1 }}>
                        Owner
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {viewer.views}
                  </TableCell>
                  <TableCell>{formatRelativeTime(viewer.lastViewedAt, now)}</TableCell>
                </TableRow>
              ))}
              {!data.viewers.length && (
                <TableRow>
                  <TableCell colSpan={3}>No views in this period.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
